import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  ScrollView,
} from '../utils/webComponents';
import { useNavigation, useRoute } from '../utils/navigation';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../hooks/useTranslation';
import { useFlashcards } from '../context/FlashcardContext';
import {
  ArrowBack,
  Add,
  Delete,
  Style,
  ChevronRight,
  PlayArrow,
  TrendingUp,
  Check,
  LibraryBooks,
} from '@mui/icons-material';
import CustomAlert from '../components/CustomAlert';

const ClassDetailScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const {
    classes,
    flashcardSets,
    studiedSets,
    currentClass,
    setCurrentClass,
    deleteClass,
    deleteFlashcardSet,
    canCreateSet,
    isPro,
    markClassFinished,
    checkAndMoveSetsToActive,
  } = useFlashcards();

  const classId = route.params?.classId;
  const [deleteSetModal, setDeleteSetModal] = useState({ visible: false, setId: null, setName: '' });
  const [alert, setAlert] = useState({ visible: false, title: '', message: '' });

  // Find the class
  const classItem = classes.find(cls => cls.id === classId);

  // Get all sets for this class (both active and studied)
  const activeSets = flashcardSets.filter(set => set.classId === classId);
  const studiedSetsForClass = (studiedSets || []).filter(set => set.classId === classId);

  // Set current class when component mounts
  useEffect(() => {
    if (classItem) {
      setCurrentClass(classItem);
    }
  }, [classId, classItem, setCurrentClass]);

  // Check and move sets to active when their review date arrives
  useEffect(() => {
    checkAndMoveSetsToActive();
  }, [checkAndMoveSetsToActive]);

  // If class not found, show error and navigate back
  useEffect(() => {
    if (!classItem && classes.length > 0) {
      setAlert({
        visible: true,
        title: 'Error',
        message: 'Class not found',
      });
      setTimeout(() => {
        navigation.goBack();
      }, 2000);
    }
  }, [classItem, classes, navigation]);

  const handleCreateSet = () => {
    if (!canCreateSet(classId)) {
      setAlert({
        visible: true,
        title: 'Limit Reached',
        message: isPro() 
          ? 'Unable to create set' 
          : 'Free users can create up to 5 sets per class. Upgrade to Pro for unlimited sets.',
      });
      return;
    }
    navigation.navigate('CreateSet', { classId });
  };

  const handleSetPress = (set) => {
    navigation.navigate('SetDetail', { setId: set.id });
  };

  const handleDeleteSet = (e, setId, setName) => {
    e.stopPropagation();
    setDeleteSetModal({ visible: true, setId, setName });
  };

  const confirmDeleteSet = () => {
    if (deleteSetModal.setId) {
      deleteFlashcardSet(deleteSetModal.setId);
      setDeleteSetModal({ visible: false, setId: null, setName: '' });
    }
  };

  const cancelDeleteSet = () => {
    setDeleteSetModal({ visible: false, setId: null, setName: '' });
  };

  const handleMarkFinished = () => {
    if (classId) {
      markClassFinished(classId, !classItem.finished);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const getDaysUntilReview = (nextReviewDate) => {
    if (!nextReviewDate) return null;
    
    // Get today's date as YYYY-MM-DD string using device's local date
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const todayString = `${year}-${month}-${day}`;
    
    // Compare date strings directly
    if (nextReviewDate <= todayString) {
      return 0; // Review date has passed or is today
    }
    
    // Calculate difference in days using local dates
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const [reviewYear, reviewMonth, reviewDay] = nextReviewDate.split('-').map(Number);
    const reviewDate = new Date(reviewYear, reviewMonth - 1, reviewDay);
    const diffTime = reviewDate - todayDate;
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    
    return Math.max(0, diffDays);
  };

  if (!classItem) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]}>
        <View style={styles.loadingContainer}>
          <Text style={[styles.loadingText, { color: theme.text }]}>
            {t('common.loading')}
          </Text>
        </View>
      </SafeAreaView>
    );
  }
  
  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <ArrowBack sx={{ fontSize: 24, color: theme.text }} />
        </TouchableOpacity>
        <View style={[styles.classCard, { backgroundColor: theme.surface, borderColor: theme.border }]}>
          <View style={[styles.classColorDot, { backgroundColor: classItem.color }]} />
          <Text style={[styles.className, { color: theme.text }]}>
            {classItem.name.toUpperCase()}
          </Text>
        </View>
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
      >
        {/* Sets to Study Section Title */}
        <Text style={[styles.sectionTitle, { color: theme.text }]}>
          {t('class.setsToStudy')}
        </Text>

        {/* Empty State */}
        {activeSets.length === 0 && (
          <View style={styles.emptyState}>
            <LibraryBooks sx={{ fontSize: 64, color: theme.textMuted }} />
            <Text style={[styles.emptyStateTitle, { color: theme.text }]}>
              {t('set.noFlashcards')}
            </Text>
            <Text style={[styles.emptyStateSubtitle, { color: theme.textSecondary }]}>
              {t('set.noFlashcardsSubtitle')}
            </Text>
          </View>
        )}

        {/* Sets List */}
        {activeSets.length > 0 && (
          <View style={styles.setsList}>
            {activeSets.map((set) => (
              <TouchableOpacity
                key={set.id}
                style={[styles.setCard, { backgroundColor: theme.surface, borderColor: theme.border }]}
                onPress={() => handleSetPress(set)}
              >
                <View style={styles.setHeader}>
                  <View style={styles.setLeft}>
                    <View style={[styles.setColorDot, { backgroundColor: set.color || '#4A90E2' }]} />
                    <View style={styles.setInfo}>
                      <Text style={[styles.setName, { color: theme.text }]}>
                        {set.name.toUpperCase()}
                      </Text>
                      {set.description && (
                        <Text style={[styles.setDescription, { color: theme.textSecondary }]}>
                          {set.description}
                        </Text>
                      )}
                    </View>
                  </View>
                  <View style={styles.setRight}>
                    <TouchableOpacity
                      style={styles.deleteSetButton}
                      onPress={(e) => handleDeleteSet(e, set.id, set.name)}
                    >
                      <Delete sx={{ fontSize: 18, color: theme.error }} />
                    </TouchableOpacity>
                    <ChevronRight sx={{ fontSize: 20, color: theme.textMuted }} />
                  </View>
                </View>
                <View style={styles.setStats}>
                  <View style={styles.setStat}>
                    <Style sx={{ fontSize: 16, color: theme.textSecondary }} />
                    <Text style={[styles.setStatText, { color: theme.textSecondary }]}>
                      {set.totalCards || 0} {t('home.stats.cards')}
                    </Text>
                  </View>
                  <View style={styles.setStat}>
                    <PlayArrow sx={{ fontSize: 16, color: theme.textSecondary }} />
                    <Text style={[styles.setStatText, { color: theme.textSecondary }]}>
                      {set.timesPlayed || 0} {t('set.plays')}
                    </Text>
                  </View>
                  <View style={styles.setStat}>
                    <TrendingUp sx={{ fontSize: 16, color: theme.textSecondary }} />
                    <Text style={[styles.setStatText, { color: theme.textSecondary }]}>
                      {Math.round(set.successRate || 0)}%
                    </Text>
                  </View>
                </View>
                <Text style={[styles.setStudyStatus, { color: theme.textMuted }]}>
                  {set.lastStudied ? `${t('home.lastStudied')} ${formatDate(set.lastStudied)}` : t('home.neverStudied')}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        )}

        {/* Studied Sets Section */}
        {studiedSetsForClass.length > 0 && (
          <>
            <Text style={[styles.sectionTitle, styles.studiedSetsTitle, { color: theme.text }]}>
              {t('class.studiedSets')}
            </Text>
            <View style={styles.setsList}>
              {studiedSetsForClass.map((set) => {
                const daysUntil = getDaysUntilReview(set.nextReviewDate);
                return (
                  <TouchableOpacity
                    key={set.id}
                    style={[styles.setCard, { backgroundColor: theme.surface, borderColor: theme.border }]}
                    onPress={() => handleSetPress(set)}
                  >
                    <View style={styles.setHeader}>
                      <View style={styles.setLeft}>
                        <View style={[styles.setColorDot, { backgroundColor: set.color || '#4A90E2' }]} />
                        <View style={styles.setInfo}>
                          <Text style={[styles.setName, { color: theme.text }]}>
                            {set.name.toUpperCase()}
                          </Text>
                          {set.description && (
                            <Text style={[styles.setDescription, { color: theme.textSecondary }]}>
                              {set.description}
                            </Text>
                          )}
                        </View>
                      </View>
                      <View style={styles.setRight}>
                        <TouchableOpacity
                          style={styles.deleteSetButton}
                          onPress={(e) => handleDeleteSet(e, set.id, set.name)}
                        >
                          <Delete sx={{ fontSize: 18, color: theme.error }} />
                        </TouchableOpacity>
                        <ChevronRight sx={{ fontSize: 20, color: theme.textMuted }} />
                      </View>
                    </View>
                    <View style={styles.setStats}>
                      <View style={styles.setStat}>
                        <Style sx={{ fontSize: 16, color: theme.textSecondary }} />
                        <Text style={[styles.setStatText, { color: theme.textSecondary }]}>
                          {set.totalCards || 0} {t('home.stats.cards')}
                        </Text>
                      </View>
                      <View style={styles.setStat}>
                        <PlayArrow sx={{ fontSize: 16, color: theme.textSecondary }} />
                        <Text style={[styles.setStatText, { color: theme.textSecondary }]}>
                          {set.timesPlayed || 0} {t('set.plays')}
                        </Text>
                      </View>
                      <View style={styles.setStat}>
                        <TrendingUp sx={{ fontSize: 16, color: theme.textSecondary }} />
                        <Text style={[styles.setStatText, { color: theme.textSecondary }]}>
                          {Math.round(set.successRate || 0)}%
                        </Text>
                      </View>
                    </View>
                    {daysUntil !== null && (
                      <Text style={[styles.setStudyStatus, { color: theme.textMuted }]}>
                        {t('class.nextReview')}: {daysUntil} {t('class.days')}
                      </Text>
                    )}
                  </TouchableOpacity>
                );
              })}
            </View>
          </>
        )}

        {/* Add a Set Button */}
        <TouchableOpacity
          style={[styles.createSetButton, { backgroundColor: theme.primary }]}
          onPress={handleCreateSet}
        >
          <Add sx={{ fontSize: 24, color: 'white' }} />
          <Text style={[styles.createSetButtonText, { color: 'white' }]}>
            {t('class.addASet')}
          </Text>
        </TouchableOpacity>

        {/* Finished Button */}
        <View style={styles.finishedButtonContainer}>
          <TouchableOpacity
            style={[styles.finishedButton, { backgroundColor: classItem.finished ? theme.border : '#50E3C2' }]}
            onPress={handleMarkFinished}
          >
            <Check sx={{ fontSize: 20, color: 'white' }} />
            <Text style={[styles.finishedButtonText, { color: 'white' }]}>
              {classItem.finished ? t('class.markActive') : t('class.markFinished')}
            </Text>
          </TouchableOpacity>
        </View>
      </ScrollView>

      {/* Delete Set Confirmation Modal */}
      {deleteSetModal.visible && (
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: theme.surface }]}>
            <Text style={[styles.modalTitle, { color: theme.text }]}>
              {t('set.deleteSet')}
            </Text>
            <Text style={[styles.modalMessage, { color: theme.textSecondary }]}>
              {t('set.deleteSetMessage')}
            </Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={[styles.modalButton, { backgroundColor: theme.surface, borderColor: theme.border }]}
                onPress={cancelDeleteSet}
              >
                <Text style={[styles.modalButtonText, { color: theme.text }]}>
                  {t('common.cancel')}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, { backgroundColor: theme.error, borderColor: theme.error }]}
                onPress={confirmDeleteSet}
              >
                <Text style={[styles.modalButtonText, { color: 'white' }]}>
                  {t('common.delete')}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
    </View>
      )}

      {/* Alert */}
      <CustomAlert
        visible={alert.visible}
        title={alert.title}
        message={alert.message}
        buttons={[
          {
            text: 'OK',
            onPress: () => {},
          },
        ]}
        onClose={() => setAlert({ ...alert, visible: false })}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  loadingContainer: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  loadingText: {
    fontSize: 18,
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 16,
  },
  backButton: {
    padding: 8,
    marginRight: 12,
  },
  classCard: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
  },
  classColorDot: {
    width: 16,
    height: 16,
    borderRadius: 8,
    marginRight: 12,
  },
  className: {
    fontSize: 18,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 100,
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 1200,
    width: '100%',
    margin: '0 auto',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 16,
    display: 'block',
    textAlign: 'left',
  },
  studiedSetsTitle: {
    marginTop: 32,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    marginBottom: 24,
  },
  emptyStateTitle: {
    fontSize: 20,
    fontWeight: '600',
    marginTop: 16,
    marginBottom: 8,
    display: 'block',
  },
  emptyStateSubtitle: {
    fontSize: 14,
    textAlign: 'center',
    display: 'block',
  },
  setsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginBottom: 24,
  },
  setCard: {
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
  },
  setHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  setLeft: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    flex: 1,
  },
  setColorDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 12,
    marginTop: 4,
  },
  setInfo: {
    flex: 1,
  },
  setName: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 4,
    letterSpacing: 0.5,
    display: 'block',
  },
  setDescription: {
    fontSize: 14,
    lineHeight: '1.4',
    display: 'block',
  },
  setRight: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  deleteSetButton: {
    padding: 4,
  },
  setStats: {
    display: 'flex',
    flexDirection: 'row',
    gap: 16,
    alignItems: 'center',
    marginBottom: 8,
  },
  setStat: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  setStatText: {
    fontSize: 14,
  },
  setStudyStatus: {
    fontSize: 14,
    display: 'block',
  },
  createSetButton: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 16,
    borderRadius: 12,
    marginTop: 8,
    marginBottom: 8,
    width: '100%',
  },
  createSetButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  finishedButtonContainer: {
    paddingHorizontal: 16,
    paddingTop: 0,
    paddingBottom: 16,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 0,
    marginBottom: 16,
  },
  finishedButton: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 16,
    borderRadius: 12,
    width: '100%',
  },
  finishedButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    borderRadius: 12,
    padding: 24,
    maxWidth: 400,
    width: '90%',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    marginBottom: 12,
    display: 'block',
  },
  modalMessage: {
    fontSize: 14,
    marginBottom: 24,
    lineHeight: '1.5',
    display: 'block',
  },
  modalButtons: {
    display: 'flex',
    flexDirection: 'row',
    gap: 12,
    justifyContent: 'flex-end',
    width: '100%',
  },
  modalButton: {
    flex: 1,
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
    borderWidth: 1,
  },
  modalButtonText: {
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
});

export default ClassDetailScreen;
